import { decodeJwt } from 'jose'
import { ACCESS_TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '~/constants'
import storage from '~/foundation/storage/localstorage'
import { refreshTokens } from './keycloack-api.refresh'

// How often to check whether the access token needs renewing.
const CHECK_INTERVAL_MS = 60_000
// Renew this long before the token actually expires, so requests in flight
// around the expiry moment still see a valid Authorization header.
const RENEW_BEFORE_EXPIRY_MS = 2 * 60_000

let intervalId: ReturnType<typeof setInterval> | null = null

function getExpiryMs(accessToken: string) {
  try {
    const { exp } = decodeJwt(accessToken)
    return exp ? exp * 1000 : null
  }
  catch {
    return null
  }
}

async function renewIfCloseToExpiry() {
  const accessToken = storage.get<string>(ACCESS_TOKEN_STORAGE_KEY)
  const refreshToken = storage.get<string>(REFRESH_TOKEN_STORAGE_KEY)
  if (!accessToken || !refreshToken)
    return

  const expiresAt = getExpiryMs(accessToken)
  if (expiresAt === null || expiresAt - Date.now() > RENEW_BEFORE_EXPIRY_MS)
    return

  try {
    await refreshTokens(refreshToken)
  }
  catch {
    // A transient failure here (e.g. a network blip) shouldn't force a
    // redirect — the reactive 401 interceptor still catches a genuinely dead
    // session on the next real request.
  }
}

/**
 * Starts periodically checking whether the access token is close to
 * expiring and renews it in the background, so users stop hitting 401s from
 * an expired token in the first place. Safe to call with no token present —
 * it becomes a no-op until a token shows up in storage.
 */
export function startSilentRefresh() {
  if (intervalId)
    return

  renewIfCloseToExpiry()
  intervalId = setInterval(renewIfCloseToExpiry, CHECK_INTERVAL_MS)
}

export function stopSilentRefresh() {
  if (!intervalId)
    return

  clearInterval(intervalId)
  intervalId = null
}
